import React, { useState, useEffect, useCallback } from "react";

import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import GetApp from "@material-ui/icons/GetApp";
import { makeStyles } from "@material-ui/core/styles";

import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const STATUS_LABELS = {
	open: { text: "🟢 Em Atendimento", bg: "#f0fdf4", color: "#166534" },
	pending: { text: "🟡 Aguardando", bg: "#fffbeb", color: "#b45309" },
	closed: { text: "🔵 Fechado", bg: "#f0f9ff", color: "#075985" },
};

const useStyles = makeStyles(theme => ({
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1, 2),
	},
	body: {
		backgroundColor: "#e9edf1",
		padding: theme.spacing(2),
		minHeight: 300,
		maxHeight: "60vh",
		overflowY: "auto",
		display: "flex",
		flexDirection: "column",
		gap: 8,
		...theme.scrollbarStyles,
	},
	bubbleRow: {
		display: "flex",
	},
	bubbleRowRight: {
		justifyContent: "flex-end",
	},
	bubble: {
		maxWidth: "75%",
		padding: "6px 10px",
		borderRadius: 8,
		fontSize: "0.85rem",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
	},
	bubbleMine: {
		backgroundColor: "#dcf8c6",
	},
	bubbleTheirs: {
		backgroundColor: "#ffffff",
	},
	timestamp: {
		display: "block",
		fontSize: "0.65rem",
		color: "#667781",
		textAlign: "right",
		marginTop: 2,
	},
	loadMoreWrap: {
		display: "flex",
		justifyContent: "center",
		padding: 8,
	},
}));

const TicketHistoryModal = ({ open, onClose, ticket }) => {
	const classes = useStyles();

	const [messages, setMessages] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open || !ticket?.id) return;
		setMessages([]);
		setPageNumber(1);
		setHasMore(false);
	}, [open, ticket]);

	useEffect(() => {
		if (!open || !ticket?.id) return;

		const fetchMessages = async () => {
			setLoading(true);
			try {
				const { data } = await api.get(`/messages/${ticket.id}`, {
					params: { pageNumber },
				});
				setMessages(prev => (pageNumber === 1 ? data.messages : [...data.messages, ...prev]));
				setHasMore(data.hasMore);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchMessages();
	}, [open, ticket, pageNumber]);

	const handleLoadMore = useCallback(() => {
		setPageNumber(prev => prev + 1);
	}, []);

	if (!ticket) return null;

	const statusInfo = STATUS_LABELS[ticket.status] || { text: ticket.status, bg: "#f1f5f9", color: "#334155" };

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<div className={classes.header}>
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon />
					</IconButton>
					<div>
						<Typography style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
							🎫 Chamado #{ticket.id}
						</Typography>
						<Typography variant="caption" style={{ color: "#64748b" }}>
							{ticket.createdAt && format(parseISO(ticket.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
						</Typography>
					</div>
				</div>
				<span
					style={{
						fontSize: "0.7rem",
						fontWeight: "bold",
						backgroundColor: statusInfo.bg,
						color: statusInfo.color,
						borderRadius: 6,
						padding: "3px 10px",
						whiteSpace: "nowrap",
					}}
				>
					{statusInfo.text}
				</span>
			</div>
			<DialogContent className={classes.body} dividers>
				{hasMore && (
					<div className={classes.loadMoreWrap}>
						<Button size="small" disabled={loading} onClick={handleLoadMore}>
							{loading ? <CircularProgress size={16} /> : "Carregar mensagens anteriores"}
						</Button>
					</div>
				)}

				{loading && messages.length === 0 && (
					<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
						<CircularProgress size={24} />
					</div>
				)}

				{!loading && messages.length === 0 && (
					<Typography variant="caption" style={{ color: "#94a3b8", textAlign: "center" }}>
						Nenhuma mensagem registrada neste chamado.
					</Typography>
				)}

				{messages.map(message => (
					<div
						key={message.id}
						className={`${classes.bubbleRow} ${message.fromMe ? classes.bubbleRowRight : ""}`}
					>
						<div className={`${classes.bubble} ${message.fromMe ? classes.bubbleMine : classes.bubbleTheirs}`}>
							{message.mediaUrl && (
								message.mediaType === "image" ? (
									<img src={message.mediaUrl} alt="mídia" style={{ maxWidth: "100%", borderRadius: 4, marginBottom: 4, display: "block" }} />
								) : (
									<Button
										size="small"
										startIcon={<GetApp />}
										href={message.mediaUrl}
										target="_blank"
										rel="noreferrer"
										style={{ textTransform: "none", marginBottom: 4 }}
									>
										Arquivo anexado
									</Button>
								)
							)}
							{message.body && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
							<span className={classes.timestamp}>
								{message.createdAt && format(parseISO(message.createdAt), "dd/MM HH:mm")}
							</span>
						</div>
					</div>
				))}
			</DialogContent>
		</Dialog>
	);
};

export default TicketHistoryModal;
